<template>
  <b-card class="shadow-sm quick-access">
    <b-card-title
      class="align-items-center d-flex justify-content-between mb-2 ml-2 mt-1"
      style="font-size: 13px"
    >
      <span>
        <b-icon icon="cursor-fill" variant="primary" class="mr-1" />
        Atalhos
      </span>
      <b-button
        v-if="editing"
        v-b-tooltip.hover
        title="Editar atalhos"
        variant="link"
        class="quick-access-edit"
        aria-label="Editar atalhos"
        @click.stop="openShortcutModal"
      >
        <b-icon icon="pencil" />
      </b-button>
    </b-card-title>

    <b-card-body>
      <div v-if="selectedShortcuts.length" class="quick-access-list">
        <button
          v-for="shortcut in selectedShortcuts"
          :key="shortcut.id"
          type="button"
          class="bg-secondary border-0 rounded p-2 text-left quick-access-item"
          @click="navigateToShortcut(shortcut.id)"
        >
          <ArrowRight class="svg-icon mr-1" :size="16" />
          <span class="font-weight-bold">{{ shortcut.shortcutLabel }}</span>
        </button>
      </div>
      <p v-else class="quick-access-empty">Nenhum atalho selecionado.</p>
    </b-card-body>

    <b-modal
      id="dashboard-shortcuts-modal"
      title="Editar atalhos"
      centered
      size="lg"
      hide-footer
      @hidden="onShortcutModalHidden"
    >
      <label for="dashboard-shortcuts-search" class="font-weight-bold">
        Buscar menu
      </label>
      <b-input-group class="mb-4">
        <b-input-group-prepend is-text>
          <b-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input
          id="dashboard-shortcuts-search"
          v-model.trim="shortcutSearch"
          type="search"
          autocomplete="off"
          placeholder="Digite o nome do menu"
        />
      </b-input-group>

      <div class="shortcut-options">
        <section
          v-for="group in filteredShortcutGroups"
          :key="group.name"
          class="shortcut-group"
        >
          <h6 class="shortcut-group-title">{{ group.name }}</h6>
          <b-form-checkbox
            v-for="shortcut in group.shortcuts"
            :key="shortcut.id"
            v-model="shortcutDraft"
            :value="shortcut.id"
            :disabled="isShortcutDisabled(shortcut.id)"
            class="shortcut-option"
          >
            {{ shortcut.label }}
          </b-form-checkbox>
        </section>

        <div v-if="!filteredShortcutGroups.length" class="shortcut-no-results">
          Nenhum menu habilitado encontrado.
        </div>
      </div>

      <div class="align-items-center d-flex justify-content-between mt-5">
        <div>
          <span class="text-muted">
            {{ shortcutDraft.length }} de {{ maxShortcuts }} atalhos
            selecionados
          </span>
          <small
            v-if="shortcutDraft.length >= maxShortcuts"
            class="d-block text-muted"
          >
            Desmarque um atalho para selecionar outro.
          </small>
        </div>
        <div>
          <b-button
            variant="light"
            class="mr-2"
            :disabled="savingShortcuts"
            @click="$bvModal.hide('dashboard-shortcuts-modal')"
          >
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            :disabled="savingShortcuts"
            @click="saveShortcuts"
          >
            <b-spinner v-if="savingShortcuts" small class="mr-1" />
            Salvar
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import ArrowRight from "vue-material-design-icons/ArrowRight";
import {
  flattenDashboardShortcutGroups,
  getAvailableDashboardShortcutGroups,
  getDashboardShortcutLocation,
  getSelectedDashboardShortcutIds,
  MAX_DASHBOARD_SHORTCUTS,
} from "../dashboardShortcuts";

export default {
  components: {
    ArrowRight,
  },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shortcutDraft: [],
      shortcutSearch: "",
      savingShortcuts: false,
      maxShortcuts: MAX_DASHBOARD_SHORTCUTS,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser || {};
    },
    availableShortcutGroups() {
      return getAvailableDashboardShortcutGroups(
        this.currentUser?.recursos || []
      );
    },
    availableShortcuts() {
      return flattenDashboardShortcutGroups(this.availableShortcutGroups);
    },
    selectedShortcutIds() {
      return getSelectedDashboardShortcutIds(
        this.currentUser?.dashboard_shortcuts,
        this.availableShortcuts
      );
    },
    selectedShortcuts() {
      const shortcutsById = new Map(
        this.availableShortcuts.map((shortcut) => [shortcut.id, shortcut])
      );

      return this.selectedShortcutIds
        .map((id) => shortcutsById.get(id))
        .filter(Boolean);
    },
    normalizedSearch() {
      return this.normalizeSearchText(this.shortcutSearch);
    },
    filteredShortcutGroups() {
      if (!this.normalizedSearch) return this.availableShortcutGroups;

      return this.availableShortcutGroups.reduce((groups, group) => {
        const groupMatches = this.normalizeSearchText(group.name).includes(
          this.normalizedSearch
        );
        const shortcuts = groupMatches
          ? group.shortcuts
          : group.shortcuts.filter((shortcut) =>
              this.normalizeSearchText(shortcut.label).includes(
                this.normalizedSearch
              )
            );

        if (shortcuts.length) {
          groups.push({ ...group, shortcuts });
        }

        return groups;
      }, []);
    },
  },
  methods: {
    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    navigateToShortcut(shortcutId) {
      this.$router.push(getDashboardShortcutLocation(shortcutId));
    },
    isShortcutDisabled(shortcutId) {
      return (
        this.shortcutDraft.length >= this.maxShortcuts &&
        !this.shortcutDraft.includes(shortcutId)
      );
    },
    openShortcutModal() {
      this.shortcutDraft = [...this.selectedShortcutIds];
      this.shortcutSearch = "";
      this.$bvModal.show("dashboard-shortcuts-modal");
    },
    onShortcutModalHidden() {
      this.shortcutSearch = "";
      this.shortcutDraft = [];
    },
    async saveShortcuts() {
      this.savingShortcuts = true;

      try {
        const { data } = await this.$http.post("/dashboard/shortcuts", {
          shortcuts: this.shortcutDraft,
        });
        const shortcuts = Array.isArray(data?.shortcuts)
          ? data.shortcuts
          : [...this.shortcutDraft];

        this.$set(this.currentUser, "dashboard_shortcuts", shortcuts);
        this.$bvModal.hide("dashboard-shortcuts-modal");
        this.$toast?.success?.("Atalhos do dashboard salvos!");
      } catch (_) {
        this.$toast?.error?.(
          "Não foi possível salvar os atalhos do dashboard."
        );
      } finally {
        this.savingShortcuts = false;
      }
    },
  },
};
</script>

<style>
.quick-access {
  background-color: color-mix(in srgb, var(--primary-color) 12%, #ffffff);
}
.quick-access .card-body {
  padding: 10px 10px 0 10px;
}
.quick-access-edit {
  align-items: center;
  color: var(--primary-color);
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;
}
.quick-access-item {
  display: block;
  color: #3f4254;
  cursor: pointer;
  font-size: 12px;
  margin: 0 0 10px !important;
  max-width: 100%;
  width: 100%;
}
.quick-access-item:hover {
  filter: brightness(0.97);
}
.quick-access-empty {
  color: #7e8299;
  font-size: 12px;
  margin: 0 0 10px;
  text-align: center;
}
.shortcut-options {
  border: 1px solid #ebedf3;
  border-radius: 8px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 0.5rem 1.25rem;
}
.shortcut-group {
  border-bottom: 1px solid #ebedf3;
  padding: 1rem 0;
}
.shortcut-group:last-child {
  border-bottom: 0;
}
.shortcut-group-title {
  color: #7e8299;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}
.shortcut-option {
  margin-bottom: 0.65rem;
}
.shortcut-option:last-child {
  margin-bottom: 0;
}
.shortcut-no-results {
  color: #7e8299;
  padding: 2rem 1rem;
  text-align: center;
}
</style>
